import { NonNegativeDecimalStringSchema } from "./decimal-string";
import { z } from "zod";

/**
 * The public identity of another Arena trader. `socialUsername` is the handle of
 * the linked social chosen by `resolveBestSocial`, withheld when the user turned
 * social visibility off. Display chain: `nickname` → `Player #<hubUserId>`.
 */
export const ArenaFriendIdentitySchema = z.object({
  hubUserId: z.number().int().positive(),
  nickname: z.string().nullable(),
  socialUsername: z.string().nullable(),
});
export type ArenaFriendIdentity = z.infer<typeof ArenaFriendIdentitySchema>;

/**
 * One row of the friends list: identity plus the two lifetime numbers the list
 * shows. Both come from the same read model the general leaderboard uses, so a
 * friend row and that user's leaderboard row can never disagree.
 */
export const ArenaFriendSchema = ArenaFriendIdentitySchema.extend({
  totalWinningsUsdt: NonNegativeDecimalStringSchema,
  duelsWon: z.number().int().nonnegative(),
});
export type ArenaFriend = z.infer<typeof ArenaFriendSchema>;

/** One pending incoming request, oldest first in the list. */
export const ArenaFriendRequestSchema = ArenaFriendIdentitySchema.extend({
  requestId: z.string(),
});
export type ArenaFriendRequestDto = z.infer<typeof ArenaFriendRequestSchema>;

/**
 * Response for `GET /v1/cede-arena/friends`: how the viewer relates to everyone,
 * without the rows. Every friends mutation answers with it; the rows come from
 * the paginated `GET /v1/cede-arena/friends/list`.
 */
export const GetArenaFriendsResponseSchema = z.object({
  /** Every friend's id, so a friend button is right for players beyond the loaded page. */
  friendHubUserIds: z.number().int().positive().array(),
  pendingRequests: ArenaFriendRequestSchema.array(),
  /**
   * Who the viewer can no longer ask: still pending, or declined (reported the
   * same, since a decline is silent). Without it an "Add friend" button offers
   * a send the API will reject.
   */
  outgoingRequestHubUserIds: z.number().int().positive().array(),
});
export type GetArenaFriendsResponse = z.infer<typeof GetArenaFriendsResponseSchema>;

const MAX_CURSOR_MS = Date.UTC(9999, 11, 31);
const MAX_INT4 = 2_147_483_647;

/** Opaque keyset cursor: `<friendship createdAt ms>.<friendId>`, each part within its column's range. */
export const ArenaFriendsCursorSchema = z
  .string()
  .regex(/^\d{1,15}\.\d{1,10}$/, { error: "malformed cursor" })
  .refine(
    (raw) => {
      const [createdAtMs, friendId] = raw.split(".").map(Number);
      return (
        createdAtMs !== undefined &&
        friendId !== undefined &&
        createdAtMs <= MAX_CURSOR_MS &&
        friendId >= 1 &&
        friendId <= MAX_INT4
      );
    },
    { error: "malformed cursor" },
  );

/** Query for `GET /v1/cede-arena/friends/list`. No cursor = the newest friendships. */
export const GetArenaFriendsListQuerySchema = z.object({
  cursor: ArenaFriendsCursorSchema.optional(),
  /** Case-insensitive substring of the friend's Arena name; friends without one never match. */
  search: z.string().trim().min(1).max(32).optional(),
});
export type GetArenaFriendsListQuery = z.infer<typeof GetArenaFriendsListQuerySchema>;

/** One page of the friends list, newest friendship first. `nextCursor` is null on the last page. */
export const GetArenaFriendsListResponseSchema = z.object({
  friends: ArenaFriendSchema.array(),
  nextCursor: ArenaFriendsCursorSchema.nullable(),
});
export type GetArenaFriendsListResponse = z.infer<typeof GetArenaFriendsListResponseSchema>;

/** Body for `POST /v1/cede-arena/friends/requests`. */
export const SendArenaFriendRequestBodySchema = z.object({
  hubUserId: z.number().int().positive(),
});
export type SendArenaFriendRequestBody = z.infer<typeof SendArenaFriendRequestBodySchema>;

/** Body for `POST /v1/cede-arena/friends/requests/respond`. */
export const RespondArenaFriendRequestBodySchema = z.object({
  requestId: z.string().min(1),
  accept: z.boolean(),
});
export type RespondArenaFriendRequestBody = z.infer<typeof RespondArenaFriendRequestBodySchema>;

/** Body for `POST /v1/cede-arena/friends/remove`. Symmetric: drops both directions. */
export const RemoveArenaFriendBodySchema = z.object({
  hubUserId: z.number().int().positive(),
});
export type RemoveArenaFriendBody = z.infer<typeof RemoveArenaFriendBodySchema>;

/**
 * Query for `GET /v1/cede-arena/friends/lookup`. EXACT nickname match only —
 * there is no prefix search, so the endpoint cannot be walked into a user
 * directory.
 */
export const LookupArenaFriendQuerySchema = z.object({
  nickname: z.string().min(1).max(32),
});
export type LookupArenaFriendQuery = z.infer<typeof LookupArenaFriendQuerySchema>;

/**
 * How the viewer already relates to a looked-up user; decides which CTA the
 * client shows. A pair the viewer asked and was declined reports `request_sent`:
 * a decline is silent, so the sender must not be able to tell the two apart.
 */
export const ArenaFriendRelationSchema = z.enum(["none", "friends", "request_sent", "request_received", "self"]);
export type ArenaFriendRelation = z.infer<typeof ArenaFriendRelationSchema>;

/** Response for `GET /v1/cede-arena/friends/lookup`. `user` is null when no nickname matches. */
export const LookupArenaFriendResponseSchema = z.object({
  user: ArenaFriendIdentitySchema.extend({ relation: ArenaFriendRelationSchema }).nullable(),
});
export type LookupArenaFriendResponse = z.infer<typeof LookupArenaFriendResponseSchema>;

/** Response for `POST /v1/cede-arena/friends/invites` — a fresh single-use link. */
export const CreateArenaFriendInviteResponseSchema = z.object({
  slug: z.string(),
});
export type CreateArenaFriendInviteResponse = z.infer<typeof CreateArenaFriendInviteResponseSchema>;

/**
 * Response for `POST /v1/cede-arena/friends/invites/:slug/claim`. `inviter` is
 * who the claimer is now friends with. Claiming is the one path that creates an
 * edge without an accept step, so the link is single-use and expires.
 */
export const ClaimArenaFriendInviteResponseSchema = z.object({
  inviter: ArenaFriendIdentitySchema,
});
export type ClaimArenaFriendInviteResponse = z.infer<typeof ClaimArenaFriendInviteResponseSchema>;
